import { useEffect, type RefObject } from 'react'
import { trackEvent } from '@/analytics'

interface YouTubeVideoData {
  title?: string
  video_id?: string
}

interface YouTubePlayer {
  destroy: () => void
  getCurrentTime: () => number
  getDuration: () => number
  getVideoData: () => YouTubeVideoData
}

interface YouTubePlayerEvent {
  data: number
  target: YouTubePlayer
}

interface YouTubePlayerErrorEvent {
  data: number
  target: YouTubePlayer
}

interface YouTubePlayerApi {
  Player: new (
    element: HTMLIFrameElement,
    options: {
      events: {
        onError: (event: YouTubePlayerErrorEvent) => void
        onReady: (event: { target: YouTubePlayer }) => void
        onStateChange: (event: YouTubePlayerEvent) => void
      }
    },
  ) => YouTubePlayer
}

declare global {
  interface Window {
    YT?: YouTubePlayerApi
    onYouTubeIframeAPIReady?: () => void
  }
}

interface UseYouTubePlayerAnalyticsOptions {
  active?: boolean
  iframeRef: RefObject<HTMLIFrameElement | null>
  placement: string
  videoId?: string
  videoName?: string
}

const PLAYER_STATE_ENDED = 0
const PLAYER_STATE_PLAYING = 1
const PLAYER_STATE_PAUSED = 2
const YOUTUBE_API_SCRIPT_ID = 'youtube-iframe-api'

let youtubeApiPromise: Promise<YouTubePlayerApi> | null = null

function loadYouTubePlayerApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise<YouTubePlayerApi>((resolve, reject) => {
    const previousReadyHandler = window.onYouTubeIframeAPIReady

    window.onYouTubeIframeAPIReady = () => {
      previousReadyHandler?.()
      if (window.YT?.Player) resolve(window.YT)
    }

    const existingScript = document.getElementById(YOUTUBE_API_SCRIPT_ID)
    if (existingScript) return

    const script = document.createElement('script')
    script.id = YOUTUBE_API_SCRIPT_ID
    script.async = true
    script.src = 'https://www.youtube.com/iframe_api'
    script.onerror = () => {
      youtubeApiPromise = null
      reject(new Error('No se pudo cargar la API de YouTube'))
    }
    document.head.appendChild(script)
  })

  return youtubeApiPromise
}

function getPlaybackPayload(
  player: YouTubePlayer,
  fallbackVideoId: string | undefined,
  fallbackVideoName: string | undefined,
  placement: string,
) {
  const videoData = player.getVideoData()
  const duration = player.getDuration()
  const currentTime = player.getCurrentTime()

  return {
    event_category: 'video',
    video_id: videoData.video_id || fallbackVideoId,
    video_name: videoData.title || fallbackVideoName || fallbackVideoId,
    video_placement: placement,
    duration_seconds: Number.isFinite(duration) ? Math.round(duration) : undefined,
    current_time_seconds: Number.isFinite(currentTime) ? Math.round(currentTime) : undefined,
  }
}

export function useYouTubePlayerAnalytics({
  active = true,
  iframeRef,
  placement,
  videoId,
  videoName,
}: UseYouTubePlayerAnalyticsOptions) {
  useEffect(() => {
    const iframe = iframeRef.current
    if (!active || !iframe) return undefined

    let cancelled = false
    let player: YouTubePlayer | null = null
    let activeVideoId = ''
    let previousState: number | null = null

    loadYouTubePlayerApi()
      .then((api) => {
        if (cancelled) return

        player = new api.Player(iframe, {
          events: {
            onReady: (event) => {
              trackEvent(
                'video_ready',
                getPlaybackPayload(event.target, videoId, videoName, placement),
              )
            },
            onStateChange: (event) => {
              const payload = getPlaybackPayload(event.target, videoId, videoName, placement)
              const currentVideoId = String(payload.video_id ?? '')

              if (event.data === PLAYER_STATE_PLAYING) {
                const isNewVideo = !activeVideoId || activeVideoId !== currentVideoId

                if (isNewVideo) {
                  if (activeVideoId) {
                    trackEvent('video_change', {
                      ...payload,
                      previous_video_id: activeVideoId,
                    })
                  }
                  activeVideoId = currentVideoId
                  trackEvent('video_start', payload)
                } else if (previousState === PLAYER_STATE_PAUSED) {
                  trackEvent('video_resume', payload)
                }
              } else if (event.data === PLAYER_STATE_PAUSED) {
                trackEvent('video_pause', payload)
              } else if (event.data === PLAYER_STATE_ENDED) {
                trackEvent('video_complete', payload)
              }

              previousState = event.data
            },
            onError: (event) => {
              trackEvent('video_error', {
                ...getPlaybackPayload(event.target, videoId, videoName, placement),
                error_code: event.data,
              })
            },
          },
        })
      })
      .catch(() => {
        trackEvent('video_api_error', {
          event_category: 'video',
          video_id: videoId,
          video_name: videoName ?? videoId,
          video_placement: placement,
        })
      })

    return () => {
      cancelled = true
      player?.destroy()
    }
  }, [active, iframeRef, placement, videoId, videoName])
}
